import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { staticRoutes, LOGIN_ROUTE } from "../config/routes";
import { useService } from "../contexts/ServiceContext";
import { useAuth } from "../contexts/AuthContext";
import RouteGuard from "./RouteGuard";

// 动态导入页面组件
const LoginPage = React.lazy(() => import("../pages/LoginPage"));
const ContainerPage = React.lazy(() => import("../pages/ContainerPage"));
const ServiceRedirect = React.lazy(() => import("./ServiceRedirect"));

// 组件映射
const componentMap = {
  LoginPage,
  ContainerPage,
  ServiceRedirect,
};

// 根路径重定向组件：根据登录状态决定跳转
const RootRedirect: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();

  // 如果正在加载认证状态，显示加载状态
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <div>正在验证身份...</div>
      </div>
    );
  }

  // 如果已登录，跳转到 /services；否则跳转到 /login
  return <Navigate to={isAuthenticated ? "/services" : LOGIN_ROUTE} replace />;
};

// 路由组件
const AppRouter: React.FC = () => {
  const { getDynamicRoutes } = useService();
  const { isAuthenticated } = useAuth();

  // 获取所有路由（静态 + 动态）
  const allRoutes = [...staticRoutes, ...getDynamicRoutes()];

  return (
    <Routes>
      {/* 根路径：根据登录状态重定向 */}
      <Route path="/" element={<RootRedirect />} />

      {/* 动态生成路由 */}
      {allRoutes.map((route) => {
        const Component =
          componentMap[route.component as keyof typeof componentMap];

        if (!Component) {
          return null;
        }

        return (
          <Route
            key={route.path}
            path={route.path}
            element={
              <RouteGuard path={route.path}>
                <React.Suspense fallback={<div>加载中...</div>}>
                  <Component />
                </React.Suspense>
              </RouteGuard>
            }
          />
        );
      })}

      {/* 对于未匹配的路径，如果已认证，尝试作为微服务路由处理 */}
      <Route 
        path="*" 
        element={
          <RouteGuard path="*">
            <React.Suspense fallback={<div>加载中...</div>}>
              {isAuthenticated ? (
                <ContainerPage />
              ) : (
                <Navigate to={LOGIN_ROUTE} replace />
              )}
            </React.Suspense>
          </RouteGuard>
        } 
      />
    </Routes>
  );
};

export default AppRouter;
